import { Bell, LifeBuoy, LogOut, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { useMyProfile } from '@/features/profile';
import { supabase } from '@/lib/supabase';

interface Row {
  to: string;
  labelKey: string;
  Icon: LucideIcon;
}

const ROWS: readonly Row[] = [
  { to: '/support', labelKey: 'nav.support', Icon: LifeBuoy },
  { to: '/notifications', labelKey: 'nav.notifications', Icon: Bell },
  { to: '/profile', labelKey: 'nav.profile', Icon: User },
];

/**
 * "More" menu (Phase 7 revamp): universal in every role's tabbar, holds the
 * persistent Support affordance the brief requires plus notifications, profile,
 * and sign-out. Keeps the tabbar to 3–4 items on mobile while still giving
 * every persona a one-tap path to file a ticket.
 */
export function MoreRoute(): JSX.Element {
  const { t } = useTranslation();
  const profile = useMyProfile().data;

  return (
    <main className="page stack">
      <h1>{t('nav.more')}</h1>
      {profile ? (
        <div className="card stack">
          <strong>{profile.fullName}</strong>
          {profile.city ? <span className="muted">{profile.city}</span> : null}
        </div>
      ) : null}
      <nav className="stack" aria-label={t('nav.more')}>
        {ROWS.map((row) => (
          <NavLink
            key={row.to}
            to={row.to}
            className="card card--interactive row"
            style={{ alignItems: 'center', gap: 'var(--space-3)', textDecoration: 'none' }}
          >
            <row.Icon size={20} aria-hidden />
            <span>{t(row.labelKey)}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className="card card--interactive row"
          style={{
            alignItems: 'center',
            gap: 'var(--space-3)',
            textAlign: 'inherit',
            color: 'var(--color-danger)',
          }}
          onClick={() => void supabase.auth.signOut()}
        >
          <LogOut size={20} aria-hidden />
          <span>{t('nav.signOut')}</span>
        </button>
      </nav>
    </main>
  );
}
